import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useStory } from '../../context/StoryContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { generateInitialPrompt } from '../../services/aiService';

import { useAuth } from '../../context/AuthContext';

const AIConfig = () => {
  const navigate = useNavigate();
  const { state, updateAI, updateAISettings, addStoryEntry, setGenerating } = useStory();
  const { user } = useAuth();
  const { ai, world, character } = state;

  React.useEffect(() => {
    if (state.supabaseStoryId && state.ownerId && user && state.ownerId !== user.id) {
      alert("Only owners can modify the story setup.");
      navigate('/story');
    }
  }, [state.supabaseStoryId, state.ownerId, user, navigate]);

  const handleSettingChange = (key, value) => updateAISettings({ [key]: value });
  const handleImageGenChange = (key, value) => updateAI({ imageGenerator: { ...ai.imageGenerator, [key]: value } });

  const handleSubmit = async (e) => {
    e.preventDefault();
    updateAI({ isMock: !ai.apiKey });
    navigate('/story');
    if (state.story.length === 0) {
      setGenerating(true);
      try {
        const initialText = await generateInitialPrompt(world, character, state.narrativeStyle, ai.settings, ai.apiKey, ai.model);
        addStoryEntry({ role: 'ai', content: initialText, timestamp: new Date().toISOString() });
      } finally {
        setGenerating(false);
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="mb-10">
          <p className="text-amber-500 text-sm font-bold tracking-widest uppercase mb-2">Final Step</p>
          <h2 className="text-4xl font-serif font-bold text-gray-900 dark:text-white">Narrative Intelligence</h2>
          <p className="text-gray-900 dark:text-purple-400 mt-2">Tune the engine that will guide your fate.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-10">
          {/* Custom API Key */}
          <div className="bg-gray-50/50 dark:bg-purple-900/20 p-8 rounded-3xl border border-gray-100 dark:border-purple-800 space-y-8 shadow-xl">
            <h4 className="text-[10px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest flex items-center space-x-2">
              <SafeIcon icon={FiIcons.FiKey} />
              <span>Custom API Key</span>
            </h4>

            {state.subscriptionTier === 'Worldbuilder' ? (
                <div className="space-y-4">
                  <label className="label-text">OpenRouter API Key (Optional)</label>
                  <input
                    type="password"
                    value={ai.apiKey || ''}
                    onChange={e => updateAI({ apiKey: e.target.value })}
                    className="input-field w-full"
                    placeholder="sk-or-v1-..."
                  />
                  <p className="text-[10px] text-gray-900 dark:text-purple-400 italic">Overrides the default platform AI model for personalized usage.</p>
                </div>
            ) : (
                <div className="text-amber-400 text-sm p-4 bg-amber-900/20 rounded-md border border-amber-500/20 flex items-center space-x-3">
                  <SafeIcon icon={FiIcons.FiLock} className="text-xl" />
                  <span>Custom API Key access is available exclusively on the Worldbuilder tier. Upgrade to unlock BYOK capabilities.</span>
                </div>
            )}
          </div>

          {/* Customization */}
          <div className="bg-gray-50/50 dark:bg-purple-900/20 p-8 rounded-3xl border border-gray-100 dark:border-purple-800 space-y-8 shadow-xl">
            <h4 className="text-[10px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest flex items-center space-x-2">
              <SafeIcon icon={FiIcons.FiSliders} />
              <span>AI Customization</span>
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex justify-between">
                  <label className="label-text">Creativity</label>
                  <span className="text-xs font-bold text-amber-600">{ai.settings.creativity}</span>
                </div>
                <input
                  type="range" min="0.1" max="1.0" step="0.1"
                  value={ai.settings.creativity}
                  onChange={e => handleSettingChange('creativity', parseFloat(e.target.value))}
                  className="w-full"
                />
                <p className="text-[10px] text-gray-900 dark:text-purple-400 italic">Lower is more focused; Higher is more unpredictable.</p>
              </div>
              <div className="space-y-4">
                <label className="label-text">Response Verbosity</label>
                <select className="input-field" value={ai.settings.verbosity} onChange={e => handleSettingChange('verbosity', e.target.value)}>
                  <option>Concise</option>
                  <option>Balanced</option>
                  <option>Descriptive</option>
                  <option>Epic / Long-form</option>
                </select>
              </div>
              <div className="space-y-4">
                <label className="label-text">Narrative Focus</label>
                <select className="input-field" value={ai.settings.focus} onChange={e => handleSettingChange('focus', e.target.value)}>
                  <option>Character-Centric</option>
                  <option>World-Centric</option>
                  <option>Plot-Driven</option>
                  <option>Dialogue-Heavy</option>
                </select>
              </div>
              <div className="space-y-4 flex items-center h-full pt-6">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ai.settings.isPaused || false}
                    onChange={e => handleSettingChange('isPaused', e.target.checked)}
                    className="w-5 h-5 rounded border-purple-300 text-amber-500 focus:ring-amber-500"
                  />
                  <span className="label-text mb-0">Pause AI Responses (Manual Mode)</span>
                </label>
              </div>
            </div>
          </div>

          {/* Image Generation */}
          <div className="bg-gray-50/50 dark:bg-purple-900/20 p-8 rounded-3xl border border-gray-100 dark:border-purple-800 space-y-8 shadow-xl">
            <h4 className="text-[10px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest flex items-center space-x-2">
              <SafeIcon icon={FiIcons.FiImage} />
              <span>Scene Visualization</span>
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4 flex items-center h-full">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ai.imageGenerator?.enabled || false}
                    onChange={e => handleImageGenChange('enabled', e.target.checked)}
                    className="w-5 h-5 rounded border-purple-300 text-amber-500 focus:ring-amber-500"
                  />
                  <span className="label-text mb-0">Enable Scene Generation</span>
                </label>
              </div>
              <div className="space-y-4">
                <label className="label-text">Art Style</label>
                <select
                  className="input-field"
                  value={ai.imageGenerator?.style || 'realistic'}
                  onChange={e => handleImageGenChange('style', e.target.value)}
                  disabled={!ai.imageGenerator?.enabled}
                >
                  <option value="realistic">Realistic</option>
                  <option value="oil painting">Oil Painting</option>
                  <option value="watercolor">Watercolor</option>
                  <option value="fantasy">Fantasy</option>
                  <option value="comic book">Comic Book</option>
                  <option value="cyberpunk">Cyberpunk</option>
                  <option value="anime">Anime</option>
                  <option value="cinematic">Cinematic</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center pt-8 border-t border-gray-100 dark:border-purple-800">
            <button type="button" onClick={() => navigate('/setup/character')} className="btn-secondary flex items-center space-x-2">
              <SafeIcon icon={FiIcons.FiArrowLeft} /><span>Back</span>
            </button>
            <button type="submit" className="btn-primary flex items-center space-x-3 px-12">
              <span className="text-lg">Awaken Weaver</span>
              <SafeIcon icon={FiIcons.FiPlay} />
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AIConfig;
